/**
 * QR 매장 기반 위치 앵커 (#271)
 *
 * 사용자가 QR 코드를 통해 매장에 진입하면, 해당 매장의 좌표를
 * sessionStorage에 저장하여 GPS 권한이 없거나 거부된 경우의
 * 거리 기반 정렬/검색 fallback으로 사용한다.
 *
 * 우선순위 (useAllStores 등 거리 의존 훅):
 *   1) URL ?lat=&lng=
 *   2) 브라우저 GPS (granted)
 *   3) QR 매장 앵커 (이 모듈)
 *   4) null → 이름순 정렬
 */

import { logger } from '@/utils/logger';

const STORAGE_KEY = 'location_anchor_v1';

/**
 * 앵커 유효 기간 (3시간).
 *
 * sessionStorage는 탭 단위라 일반적으로 자연 소멸하나, 장시간 탭을 열어둔
 * 사용자가 며칠 전 다른 도시 매장 좌표를 기준점으로 거리 정렬받는 상황을
 * 방지하기 위한 안전 장치. 만료 시 조회 시점에 정리.
 */
export const ANCHOR_TTL_MS = 3 * 60 * 60 * 1000;

export interface LocationAnchor {
  lat: number;
  lng: number;
  /** 앵커 출처 (디버깅/표시용) */
  source: 'qr-pickup' | 'qr-table';
  /** 출처 매장 ID */
  storeId: number;
  /** 저장 시각 (epoch ms) */
  ts: number;
}

function isFiniteCoord(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isValidLat(lat: number): boolean {
  return lat >= -90 && lat <= 90;
}

function isValidLng(lng: number): boolean {
  return lng >= -180 && lng <= 180;
}

/**
 * 매장 좌표를 앵커로 저장.
 * 좌표가 유효하지 않으면 no-op.
 */
export function setLocationAnchor(input: {
  lat: number | null | undefined;
  lng: number | null | undefined;
  source: LocationAnchor['source'];
  storeId: number;
}): void {
  if (typeof window === 'undefined') return;
  if (!isFiniteCoord(input.lat) || !isFiniteCoord(input.lng)) return;
  if (!isValidLat(input.lat) || !isValidLng(input.lng)) return;

  const anchor: LocationAnchor = {
    lat: input.lat,
    lng: input.lng,
    source: input.source,
    storeId: input.storeId,
    ts: Date.now(),
  };

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(anchor));
  } catch (err) {
    logger.warn('[locationAnchor] sessionStorage 저장 실패:', err);
  }
}

/**
 * 저장된 앵커 조회. 유효하지 않거나 TTL 만료 시 자동 정리 후 null.
 */
export function getLocationAnchor(): LocationAnchor | null {
  if (typeof window === 'undefined') return null;

  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch (err) {
    logger.warn('[locationAnchor] sessionStorage 조회 실패:', err);
    return null;
  }
  if (raw === null) return null;

  let parsed: Partial<LocationAnchor> | null = null;
  try {
    parsed = JSON.parse(raw) as Partial<LocationAnchor>;
  } catch (err) {
    logger.warn('[locationAnchor] 손상된 앵커 JSON — 정리:', err);
  }

  if (
    parsed &&
    isFiniteCoord(parsed.lat) &&
    isFiniteCoord(parsed.lng) &&
    isValidLat(parsed.lat) &&
    isValidLng(parsed.lng) &&
    typeof parsed.storeId === 'number' &&
    (parsed.source === 'qr-pickup' || parsed.source === 'qr-table') &&
    typeof parsed.ts === 'number'
  ) {
    // TTL 검사 — 만료되었으면 정리하고 null 반환
    if (Date.now() - parsed.ts > ANCHOR_TTL_MS) {
      logger.warn('[locationAnchor] TTL 만료 — 정리', {
        ageMs: Date.now() - parsed.ts,
        storeId: parsed.storeId,
      });
      clearLocationAnchor();
      return null;
    }
    return parsed as LocationAnchor;
  }

  // 스키마 위반 — 정리
  if (parsed !== null) {
    logger.warn('[locationAnchor] 스키마 위반 — 정리', { raw });
  }
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  return null;
}

export function clearLocationAnchor(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
